import PayoutBatchStatusBadge from '@/components/payouts/PayoutBatchStatusBadge';
import PaginationControls from '@/components/shared/PaginationControls';
import TableEmptyState from '@/components/table/TableEmptyState';
import TableSkeleton from '@/components/table/TableSkeleton';
import {
  TableScrollArea,
  stickyColumnProps,
  stickyEndColumnProps,
} from '@/components/table/TableScrollArea';
import Money from '@/components/text/money';
import { useDownloadPayoutInvoiceAction } from '@/hooks/actions/use-download-payout-invoice-action';
import type { Pagination } from '@/types/pagination';
import { Box, Button, Flex, Table, Text } from '@chakra-ui/react';
import type { PayoutBatchListItem } from '@fluenceur/types';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, DownloadDataWindow } from 'iconoir-react';

interface InvoicesTableProps {
  invoices: PayoutBatchListItem[];
  pagination?: Pagination | null;
  loading?: boolean;
  onPageChange?: (page: number) => void;
}

export const InvoicesTable: React.FC<InvoicesTableProps> = ({
  invoices,
  pagination,
  loading = false,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const { downloadInvoice, isDownloading } = useDownloadPayoutInvoiceAction();

  return (
    <Box pb={8}>
      {loading ? (
        <TableSkeleton
          rows={8}
          columns={[
            { width: '220px' },
            { width: '160px', type: 'date' },
            { width: '140px' },
            { width: '140px' },
            { width: '132px' },
          ]}
          stickyColumns={1}
          stickyLastColumn
          firstColumnWidth="220px"
          lastColumnWidth="132px"
        />
      ) : (
        <TableScrollArea>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader width="220px" {...stickyColumnProps('220px')}>
                  {t('commissions.invoices.table.reference')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="160px">
                  {t('commissions.invoices.table.issueDate')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="140px">
                  {t('commissions.invoices.table.amount')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="140px">
                  {t('commissions.invoices.table.status')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="132px" {...stickyEndColumnProps('132px')} />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {invoices.length === 0 ? (
                <TableEmptyState
                  colSpan={5}
                  icon={<DownloadDataWindow width={22} height={22} />}
                  title={t('commissions.invoices.empty')}
                />
              ) : (
                invoices.map((invoice) => (
                  <Table.Row key={invoice.id} _hover={{ bg: 'gray.50' }}>
                    <Table.Cell width="220px" {...stickyColumnProps('220px')}>
                      <Text fontSize="sm" fontFamily="mono">
                        {invoice.invoiceReference || '-'}
                      </Text>
                    </Table.Cell>
                    <Table.Cell width="160px">
                      <Flex direction="column">
                        <Text fontSize="sm">
                          {formatDate(invoice.completedAt || invoice.createdAt)}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {formatTime(invoice.completedAt || invoice.createdAt)}
                        </Text>
                      </Flex>
                    </Table.Cell>
                    <Table.Cell width="140px">
                      <Money
                        fontSize="14px"
                        value={invoice.totalChargeAmount.amount}
                        currency={invoice.totalChargeAmount.currencyCode}
                      />
                    </Table.Cell>
                    <Table.Cell width="140px">
                      <PayoutBatchStatusBadge
                        status={invoice.status}
                        fundedAt={invoice.fundedAt}
                        lastPaymentFailedAt={invoice.lastPaymentFailedAt}
                        closedReason={invoice.closedReason}
                        size="sm"
                      />
                    </Table.Cell>
                    <Table.Cell width="132px" textAlign="right" {...stickyEndColumnProps('132px')}>
                      <Button
                        size="xs"
                        variant="outline"
                        colorPalette="gray"
                        onClick={() => downloadInvoice(invoice.id)}
                        disabled={isDownloading}
                      >
                        <Download width={14} height={14} style={{ marginRight: '4px' }} />
                        {t('commissions.invoices.table.download')}
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </TableScrollArea>
      )}

      {pagination && onPageChange && pagination.total > 1 && (
        <PaginationControls
          pagination={pagination}
          onPageChange={onPageChange}
          isLoading={loading}
        />
      )}
    </Box>
  );
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'Invalid Date';
  }
};

const formatTime = (dateString: string | null): string => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};
